/**
 * Translating the terminal's own texts (not the data from the API: products, addresses, order statuses, ...).
 *
 * The language follows the locale of the last user that logged in (`GET /api/v2/whoami`, kept in the session, see
 * `getLocale` in `session.ts`), so the login page and the PIN prompt after the idle lock speak the language of whoever
 * used the terminal last. The catalogues live in `i18n/`: Dutch (`nl.ts`) is the source and defines the keys, the other
 * languages are typed against it.
 *
 * Messages can hold `{name}` placeholders, filled from the params. A plural message has a form per plural category of
 * `Intl.PluralRules` and is picked by the `count` param (which also fills `{count}`).
 *
 *     t('order.created', {date: order.created})
 *     t('update.minutes', {count: 5})
 *
 * Function components get `t` from `useTranslation` (`hooks/use-translation.ts`) to re-render on a language change;
 * code outside of rendering (event handlers, helpers) can call the `t` exported here, it uses the current language.
 */
import {de} from '../i18n/de';
import {en} from '../i18n/en';
import {fr} from '../i18n/fr';
import type {Catalogue, PluralMessage} from '../i18n/nl';
import {nl} from '../i18n/nl';
import {getLocale, subscribe} from './session';

export type Language = 'nl' | 'en' | 'de' | 'fr';

/** Before anybody logged in on this terminal, and for a locale in a language there is no catalogue for. */
export const defaultLanguage: Language = 'nl';

const catalogues: Record<Language, Catalogue> = {nl, en, de, fr};

/** Every language in its own words, for the user to pick one: not translated, a user recognises their own language. */
export const languageNames: Record<Language, string> = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
};

/** The flag of every language, a `flag-icons` country code (`fi fi-gb`): a flag is recognised before its name is read. */
export const languageFlags: Record<Language, string> = {
    nl: 'nl',
    en: 'gb',
    de: 'de',
    fr: 'fr',
};

/**
 * The flag of a locale: the country it names (`nl_BE` → `be`, `en_US` → `us`), else the flag of its language (`en` →
 * `gb`, also for a language there is no catalogue for: the default language's).
 */
export const toFlag = (locale: string | null | undefined): string => {
    const country = /^[a-z]{2,3}[_-]([a-z]{2})$/i.exec(locale ?? '')?.[1];

    return country ? country.toLowerCase() : languageFlags[toLanguage(locale)];
};

export type MessageKey = keyof Catalogue;

export type MessageParams = Record<string, string | number>;

export type Translate = (key: MessageKey, params?: MessageParams) => string;

const isLanguage = (value: string): value is Language => Object.hasOwn(catalogues, value);

/** The language of an API locale (`nl_BE` → `nl`, `fr_FR` → `fr`), the default language for anything else. */
export const toLanguage = (locale: string | null | undefined): Language => {
    const language = (locale ?? '').split(/[_-]/)[0].toLowerCase();

    return isLanguage(language) ? language : defaultLanguage;
};

/**
 * The language the terminal speaks right now. It changes with the session's locale: `subscribe` of `session.ts` tells,
 * this is the snapshot of `useSyncExternalStore` (see `useTranslation`).
 */
export const getLanguage = (): Language => toLanguage(getLocale());

const pluralRules = new Map<Language, Intl.PluralRules>();

const pickPluralForm = (language: Language, message: PluralMessage, count: number): string => {
    let rules = pluralRules.get(language);

    if (!rules) {
        rules = new Intl.PluralRules(language);
        pluralRules.set(language, rules);
    }

    return message[rules.select(count)] ?? message.other;
};

/** The message in the given language, with its placeholders filled; an unknown placeholder is left as it is. */
export const translate = (language: Language, key: MessageKey, params: MessageParams = {}): string => {
    const message = catalogues[language][key];
    const text = 'string' === typeof message ? message : pickPluralForm(language, message, Number(params.count ?? 0));

    return text.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
        Object.hasOwn(params, name) ? String(params[name]) : placeholder,
    );
};

/** One function per language: the same one every time, so it changes (as a hook dependency) with the language only. */
const translators = Object.fromEntries(
    Object.keys(catalogues).map(language => [
        language,
        (key: MessageKey, params?: MessageParams): string => translate(language as Language, key, params),
    ]),
) as Record<Language, Translate>;

/** The translator for a language, see `useTranslation`. */
export const getTranslator = (language: Language): Translate => translators[language];

/** Translate into the current language: for code outside of rendering, components use `useTranslation`. */
export const t: Translate = (key, params) => translate(getLanguage(), key, params);

// screen readers, hyphenation and the browser's own texts (e.g. form validation) follow the language of the page; set
// once here, next to the language, instead of in a component that StrictMode mounts twice
const applyDocumentLanguage = (): void => {
    document.documentElement.lang = getLanguage();
};

applyDocumentLanguage();
subscribe(applyDocumentLanguage);
